import React, { useState } from 'react'

import { useScreen } from '@hozana/screen/useScreen'
import { TRACKING_SOURCE, UTM } from '@hozana/tracking/constants'

import { AbsoluteDiv } from 'elements/layout/AbsoluteDiv'
import { Cell } from 'elements/layout/Cell'
import { Div } from 'elements/layout/Div'
import { Row } from 'elements/layout/Row'

import { Sidebar } from 'general/structure/Sidebar'

import type { TCommunity } from 'modules/community/types'
import type { TPublication } from 'modules/publication/types'
import { BigShareButton } from 'modules/share/components/BigShareButton'
import { ShareContacts } from 'modules/share/components/ShareContacts'
import { ShareCopy } from 'modules/share/components/ShareCopy'
import { ShareCount } from 'modules/share/components/ShareCount'
import { ShareMore } from 'modules/share/components/ShareMore'
import { ShareProvider } from 'modules/share/components/ShareProvider'
import { ShareSocial } from 'modules/share/components/ShareSocial'
import { SHARE_OBJECTS, SHARE_SOCIAL_SIZE, SHARE_TYPES, TSocialShareType } from 'modules/share/constants'

type TSharePublicationButtonsProps = {
  community: TCommunity
  publication: TPublication
}

export const SharePublicationButtons: TFC<TSharePublicationButtonsProps> = ({ community, publication }) => {
  const screen = useScreen()
  const [isExpandedShareCopy, expandShareCopy] = useState<boolean>()

  const bigShareButton = screen.xs
    ? ({
        medium: SHARE_TYPES.WHATSAPP,
        text: 'trans:publication:share.wording.share-on-whatsapp',
      } as const)
    : ({
        medium: SHARE_TYPES.FACEBOOK,
        text: 'trans:publication:share.wording.share-on-facebook',
      } as const)

  return (
    <ShareProvider
      objectToShare={SHARE_OBJECTS.PUBLICATION}
      trackingSource={TRACKING_SOURCE.PUBLICATION_SIDEBAR}
      utmContent={UTM.CONTENT.PUBLICATION_PAGE_SHARE_BUTTON}
      community={community}
      publication={publication}
    >
      {/* Facebook / Whatsapp big buttons */}
      <Div noPrint>
        <BigShareButton medium={bigShareButton.medium}>{bigShareButton.text}</BigShareButton>
      </Div>
      {screen.xs || screen.sm || screen.md ? (
        /** Screen is defined to "xs: true" by default in SSR, even on desktop,
         * so we force that component to be visible only on mobile, by CSS
         */
        <Row p="5px" align="middleBetween" noPrint visible={['xs', 'sm', 'md']}>
          <Cell h="40px" align="middleCenter" w={{ xs: 1, sm: 'auto' }} order={{ xs: '1', sm: '0' }}>
            {!isExpandedShareCopy && (
              <>
                <ShareSocial
                  shareTypes={(
                    [
                      SHARE_TYPES.WHATSAPP,
                      SHARE_TYPES.MESSENGER,
                      SHARE_TYPES.FACEBOOK,
                      SHARE_TYPES.TWITTER,
                      SHARE_TYPES.PINTEREST,
                    ] as TSocialShareType[]
                  ).filter((medium) => medium !== bigShareButton.medium)}
                />
                <ShareContacts />
              </>
            )}
            <ShareCopy m="5px" onShare={() => expandShareCopy(false)} onExpand={() => expandShareCopy(true)} />
            {!isExpandedShareCopy && <ShareMore />}
          </Cell>
          <Cell w={{ xs: 1, sm: 'auto' }} align="center">
            <ShareCount counts={publication.sharesCount} />
          </Cell>
        </Row>
      ) : (
        <AbsoluteDiv top h="100%" right="-265px" noPrint>
          <Sidebar>
            <Div p="0 0 0 20px" align="topCenter" column>
              {!community.isPrivate && publication.sharesCount && publication.sharesCount.total !== 0 && (
                <Div m="0 0 10px 5px">
                  <ShareCount counts={publication.sharesCount} />
                </Div>
              )}
              <ShareSocial
                vertical
                size={SHARE_SOCIAL_SIZE.BIG}
                shareTypes={[
                  SHARE_TYPES.FACEBOOK,
                  SHARE_TYPES.TWITTER,
                  SHARE_TYPES.PINTEREST,
                  SHARE_TYPES.WHATSAPP,
                  SHARE_TYPES.MESSENGER,
                ]}
              />
              <ShareContacts size={SHARE_SOCIAL_SIZE.BIG} />
              <ShareCopy size={SHARE_SOCIAL_SIZE.BIG} m="3px 6px" />
            </Div>
          </Sidebar>
        </AbsoluteDiv>
      )}
    </ShareProvider>
  )
}
